import { sandboxFetch } from "sandbox/api/client";

interface CachedToken {
  expiresAt: number;
  token: string;
}

const cache = new Map<string, CachedToken>();

interface TokenResponse {
  access_token: string;
  expires_in: number;
  token_type: string;
}

const exchangeClientCredentials = async (credential: {
  clientId: string;
  clientSecret: string;
}): Promise<TokenResponse> => {
  const response = await sandboxFetch("/auth/token", {
    method: "POST",
    body: {
      grant_type: "client_credentials",
      client_id: credential.clientId,
      client_secret: credential.clientSecret,
    },
    auth: { mode: "no-auth" },
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(
      (body as { error_description?: string }).error_description ??
        "Failed to obtain client token",
    );
  }

  return response.json() as Promise<TokenResponse>;
};

export const getClientToken = async (credential: {
  clientId: string;
  clientSecret: string;
}): Promise<string> => {
  const cached = cache.get(credential.clientId);
  if (cached && Date.now() < cached.expiresAt) {
    return cached.token;
  }

  const data = await exchangeClientCredentials(credential);

  cache.set(credential.clientId, {
    token: data.access_token,
    expiresAt: Date.now() + data.expires_in * 1000,
  });

  return data.access_token;
};

export const clearClientTokenCache = (): void => {
  cache.clear();
};
